/* ============================================================
   AJAX cart.

   Every cart mutation in the theme goes through here and nothing
   navigates: adding from a product form, the quantity steppers and
   remove links on /cart, and "clear cart".

   Three things are worth knowing before changing this file.

   1. Requests are serialised (see `queue`). Shopify's cart endpoints are
      not transactional with respect to each other — firing three
      /cart/change.js calls from three fast clicks on "+" resolves them
      against three different starting states and the last one wins with
      a stale line index. One at a time is not a performance compromise,
      it is the only correct order.

   2. The cart's two surfaces — the /cart page and the side drawer —
      re-render themselves from Liquid via the Section Rendering API
      rather than being patched in JS. Money formatting, discount
      allocations and the line lists are already expressed in
      sections/cart.liquid and sections/cart-drawer.liquid; re-deriving
      them here would mean two implementations that drift.

   3. Everything is delegated from `document`, never bound per element.
      Those sections are replaced wholesale on every update, so directly
      bound listeners would be discarded with the markup they were on.
      For the same reason the drawer's open/closed state is a class on
      <html> rather than on anything inside the drawer.

   The header count is the one thing patched directly rather than
   re-rendered: it is a single number, and a section fetch to move it
   would double every request in the theme.
   ============================================================ */

const ROUTES = window.groovyCartRoutes || {};
const CART_URL = ROUTES.cart || '/cart';

/* The .js endpoints are the Liquid routes with a suffix — going through
   `routes` rather than hardcoding keeps this working on the localised
   storefronts, where the cart lives under /en-ca/cart and friends. */
const ADD_URL = (ROUTES.add || '/cart/add') + '.js';
const CHANGE_URL = (ROUTES.change || '/cart/change') + '.js';
const CLEAR_URL = (ROUTES.clear || '/cart/clear') + '.js';
const STATE_URL = CART_URL + '.js';

const JSON_HEADERS = {
  'Content-Type': 'application/json',
  Accept: 'application/json'
};

/* ---------- serialised requests ---------- */

let queue = Promise.resolve();

/** Run `job` after every job already queued, whatever their outcome. */
function serial(job) {
  const run = queue.then(job, job);
  // keep the chain alive after a rejection, but let the caller still see it
  queue = run.catch(() => {});
  return run;
}

/* ---------- the cart ---------- */

async function readCart() {
  /* cache: 'no-store' keeps the BROWSER from serving this out of its own
     cache; the appended timestamp keeps the URL from ever repeating at
     all, which also defeats any cache sitting between here and Shopify
     that doesn't fully honour no-store (a CDN, a proxy) — belt and
     suspenders, since a stale cart read here is exactly what showed up as
     the header count or the order summary not moving until a real
     reload. */
  const res = await fetch(`${STATE_URL}?_=${Date.now()}`, { headers: JSON_HEADERS, cache: 'no-store' });
  if (!res.ok) throw new Error('could not read the cart');
  return res.json();
}

async function post(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(body)
  });
  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    /* Shopify answers 422 for "you asked for more than we have" and puts
       the shopper-facing sentence in `description`. Surfacing that beats
       any message written here — it names the product and the stock. */
    throw new Error(data.description || data.message || 'the cart could not be updated');
  }
  return data;
}

/* ---------- the header count ----------
   cart.item_count is the TOTAL QUANTITY across the cart, not the number
   of line items (that is cart.items.length). Three of one product is 3
   here, which is the number the badge is supposed to show. */

function paintCount(count) {
  document.querySelectorAll('[data-cart-count]').forEach(el => {
    el.textContent = count;
    el.toggleAttribute('hidden', count === 0);
  });
}

/* ---------- the cart surfaces ----------
   Two sections can render the cart, and both are re-rendered from Liquid
   rather than patched in JS, so money formatting and discount allocations
   stay expressed in one place:

   · the /cart page          sections/cart.liquid       [data-cart-page]
   · the side drawer         sections/cart-drawer.liquid [data-cart-drawer]

   The drawer sits in the header group, so it is on every page — which is
   why this is a list and not the single section it used to be. On /cart
   both are present, and both are kept current: a shopper can still open the
   drawer from the header icon while standing on the cart page. */

function cartSections() {
  const found = [];
  document.querySelectorAll('[data-cart-page], [data-cart-drawer]').forEach(root => {
    const section = root.closest('.shopify-section');
    if (section && section.id && !found.includes(section)) found.push(section);
  });
  return found;
}

/** "shopify-section-main" → "main". Shopify's `sections` parameter wants
 *  exactly this form: no shopify-section- prefix and no leading #. */
function sectionId(section) {
  return section.id.replace(/^shopify-section-/, '');
}

/** Every cart surface currently on the page, as the comma-joined ids that
 *  parameter takes — or '' where there are none. */
function cartSectionIds() {
  return cartSections().map(sectionId).join(',');
}

/** Unwraps and injects a section's rendered HTML — the API answers with
 *  the section's own #shopify-section-id wrapper included, so this uses
 *  that inner content rather than assigning the whole thing, which would
 *  nest a second #shopify-section-main inside the first on every update. */
function injectSection(section, html) {
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const fresh = parsed.getElementById(section.id);
  section.innerHTML = fresh ? fresh.innerHTML : html;
}

/** Re-renders every cart surface on the page.
 *
 *  `payload` is the optional `sections` object Shopify's cart endpoints
 *  return when the request that mutated the cart included a `sections` key
 *  in its body. Taking the HTML from there — rather than always following
 *  up with a second, separate fetch — means the DOM can never render an
 *  older cart state than the mutation that just happened: there is no
 *  second request to race against, and no cache (browser or otherwise) it
 *  could be served from. See changeLine() and the clear handler below,
 *  which are what ask for this payload in the first place.
 *
 *  Whatever the payload didn't carry is fetched instead. That is the normal
 *  case for the add path, where the state comes from readCart() — a plain
 *  GET, which cannot ask for sections. */
async function renderSections(payload) {
  const missing = [];

  for (const section of cartSections()) {
    const html = payload && payload[sectionId(section)];
    if (typeof html !== 'string') {
      missing.push(section);
      continue;
    }
    injectSection(section, html);
  }

  if (missing.length) await fetchSections(missing);

  await bindCards();
}

/** Fallback path: one request for however many surfaces still need one.
 *  The sections are passed as elements rather than ids because injecting
 *  into one doesn't disturb the others, so the references stay good for
 *  the whole loop. */
async function fetchSections(sections) {
  const ids = sections.map(sectionId).join(',');
  /* no-store + a timestamp: this hits what would otherwise be the exact
     same URL every time, so nothing (the browser's own cache, or a proxy
     between here and Shopify that doesn't fully honour no-store) can
     hand back a response older than the request that just fired. */
  /* No Accept header. `/cart` answers the cart OBJECT to a request that
     asks for JSON and ignores `sections` entirely, so asking for JSON here
     -- the obvious thing, since the section map comes back as JSON -- is
     what makes the map arrive as a cart and every id read as missing.
     Left alone, the route serves the section map the API documents. */
  const res = await fetch(`${CART_URL}?sections=${encodeURIComponent(ids)}&_=${Date.now()}`, {
    cache: 'no-store'
  });
  if (!res.ok) throw new Error('could not re-render the cart');

  const payload = await res.json();
  for (const section of sections) {
    const html = payload[sectionId(section)];
    if (typeof html !== 'string') throw new Error('cart section missing from the response');
    injectSection(section, html);
  }
}

/* The last line removed turns this page into the empty state, which
   renders the drinks as 3D cards. Markup assigned through innerHTML never
   runs its own <script>, so the module either gets re-scanned (it is
   already on the page) or imported now — its URL rides on the grid,
   because an asset here cannot ask Liquid for it. */

async function bindCards() {
  /* Scoped to the document, and called ONCE per render rather than once per
     cart surface: the refresh below has to dispose the grid when the cart
     goes the other way — the last update left a root that is no longer in
     the page, and with it a WebGL context the browser counts against a cap
     of ~16 — and running that twice for one update would tear the grid down
     and rebuild it for nothing. */
  if (window.groovyCardsRefresh) {
    window.groovyCardsRefresh(document);
    return;
  }

  const grid = document.querySelector('[data-groovy-cards]');
  if (!grid) return;

  const src = grid.dataset.cardsSrc;
  if (!src) return;
  try {
    await import(src);   // the module scans the document as it loads
  } catch {
    /* Cards are decoration on a cart that is already correct — a failed
       import must not take the rest of the update down with it. */
  }
}

/** The one place anything reacts to the cart having changed. */
async function settle(cart) {
  paintCount(cart.item_count);
  await renderSections(cart.sections);
  document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
}

/* ---------- the side cart ----------
   The panel itself is sections/cart-drawer.liquid; everything that opens or
   closes it is here, for the same reason as the rest of this file — the
   drawer's markup is replaced wholesale on every cart change, so a listener
   bound to anything inside it would be discarded with the markup it was on.

   The OPEN STATE is a class on <html> for that same reason: an element
   inside the drawer would lose it mid-render, which is exactly when the
   drawer is open and being looked at. The CSS in cart-drawer.liquid reads
   it from there.

   Nothing opens the drawer on its own on page load. It opens when an add
   succeeds, and when the shopper asks for it. */

const DRAWER_CLASS = 'cart-drawer-open';

/* Everything focusable the panel can contain. Excludes tabindex="-1", which
   is how the drawer's thumbnail links opt out — they point at the same
   product as the title link right beside them, so keeping both would make
   every line two identical tab stops. */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), textarea, select, summary, [tabindex]:not([tabindex="-1"])';

/* Whatever had focus when the drawer opened — the cart icon, or the add
   button on a PDP. Sending focus back there on close is what keeps a
   keyboard user's place on the page. */
let returnFocusTo = null;

function drawer() {
  return document.querySelector('[data-cart-drawer]');
}

function drawerIsOpen() {
  return document.documentElement.classList.contains(DRAWER_CLASS);
}

/** True if the drawer actually opened. False means there is no drawer on
 *  this page, and the caller should do whatever it would have done without
 *  one — for the header's cart icon, that is following its own href. */
function openDrawer(trigger) {
  const root = drawer();
  if (!root) return false;

  /* Already open — a second add while the panel is on screen. The panel has
     just re-rendered itself with the new line, so there is nothing to do:
     moving focus again would drag a keyboard user back to the close button
     mid-read, and overwriting the return target would strand them wherever
     the second add happened to come from. */
  if (drawerIsOpen()) return true;

  returnFocusTo = trigger || document.activeElement;

  /* Lets the mobile nav drawer close first and hand over the scroll lock on
     <html> — see the listener for this in sections/header.liquid. Without
     it, closing this drawer would leave the page unscrollable with the nav
     still open underneath. */
  document.dispatchEvent(new CustomEvent('cart-drawer:open'));

  const close = root.querySelector('button[data-cart-drawer-close]');
  const panel = root.querySelector('[data-cart-drawer-panel]');

  /* A transition needs a FROM value the browser has actually computed. On
     the add path this panel is a few milliseconds old -- the drawer just
     re-rendered itself with the new line, which replaced its markup -- so
     adding the class now would be the first style this element ever gets,
     and it would be born open: no slide, the panel just appears. Reading a
     layout property first forces the closed state to be computed, and the
     class below then has something to animate away from.

     Opening from the header icon never hit this, since that panel has been
     sitting in the page styled since load. */
  if (panel) void panel.offsetWidth;

  document.documentElement.classList.add(DRAWER_CLASS);

  /* Next frame: until the class above has been styled in, the panel is
     still visibility: hidden, and focus() on a hidden element does
     nothing at all. */
  window.requestAnimationFrame(() => {
    const target = close || panel;
    if (target) target.focus();
  });

  return true;
}

function closeDrawer() {
  if (!drawerIsOpen()) return;

  /* Read before the class comes off: after it, the panel is on its way to
     visibility: hidden and activeElement is about to be dropped to <body>,
     which is the thing this is here to prevent. */
  const root = drawer();
  const hadFocus = !!(root && root.contains(document.activeElement));

  document.documentElement.classList.remove(DRAWER_CLASS);

  if (hadFocus && returnFocusTo && returnFocusTo.isConnected) returnFocusTo.focus();
  returnFocusTo = null;
}

document.addEventListener('click', event => {
  const open = event.target.closest('[data-cart-drawer-open]');
  if (open) {
    /* A modified click on the cart icon is the shopper asking for the cart
       PAGE, in a tab or a window. Let the anchor do its job. */
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (openDrawer(open)) event.preventDefault();
    return;
  }

  /* The backdrop carries this too, so a click on the dimmed page beside the
     panel closes it — the same affordance as the nav drawer. */
  const close = event.target.closest('[data-cart-drawer-close]');
  if (close) {
    event.preventDefault();
    closeDrawer();
  }
});

document.addEventListener('keydown', event => {
  if (!drawerIsOpen()) return;

  if (event.key === 'Escape') {
    closeDrawer();
    return;
  }

  /* Keeps Tab inside the panel. Without this the next Tab off the last
     control walks into the page behind the drawer, which is dimmed and
     scroll-locked — the focus ring goes somewhere the shopper cannot see
     and cannot scroll to. */
  if (event.key !== 'Tab') return;

  const root = drawer();
  const panel = root && root.querySelector('[data-cart-drawer-panel]');
  if (!panel) return;

  // offsetParent filters out anything inside a collapsed <details>
  const stops = Array.from(panel.querySelectorAll(FOCUSABLE)).filter(el => el.offsetParent !== null);
  if (!stops.length) return;

  const first = stops[0];
  const last = stops[stops.length - 1];
  const inside = panel.contains(document.activeElement);

  if (event.shiftKey && (!inside || document.activeElement === first)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && (!inside || document.activeElement === last)) {
    event.preventDefault();
    first.focus();
  }
});

/* ---------- add to cart ---------- */

const BUSY = 'is-adding';
const DONE = 'is-added';

function feedback(button, state, ms) {
  if (!button) return;
  button.classList.remove(BUSY, DONE);
  if (state) button.classList.add(state);
  if (!ms) return;
  window.setTimeout(() => button.classList.remove(state), ms);
}

function showError(form, message) {
  let box = form.querySelector('[data-cart-error]');
  if (!box) {
    box = document.createElement('p');
    box.className = 'cart-error';
    box.setAttribute('data-cart-error', '');
    box.setAttribute('role', 'alert');
    form.appendChild(box);
  }
  box.textContent = message;
  box.hidden = false;
}

function clearError(form) {
  const box = form.querySelector('[data-cart-error]');
  if (box) box.hidden = true;
}

document.addEventListener('submit', event => {
  const form = event.target.closest('form[action*="/cart/add"]');
  if (!form) return;

  event.preventDefault();
  clearError(form);

  const button = form.querySelector('[type="submit"]');
  const fields = new FormData(form);

  /* FormData carries the quantity input's real value, so a shopper who
     set 3 on the PDP adds 3 — the same reason the button is disabled
     below, since a second submit mid-flight would add another 3.
     A form with no quantity field at all (the product tabs, the showcase
     cards) falls through to 1, which is what it was always adding. */
  const line = { id: fields.get('id'), quantity: Number(fields.get('quantity')) || 1 };

  // subscriptions: dropping this would silently turn a subscription into
  // a one-off purchase, which is worse than failing
  if (fields.get('selling_plan')) line.selling_plan = fields.get('selling_plan');

  const properties = {};
  for (const [key, value] of fields.entries()) {
    const match = key.match(/^properties\[(.+)\]$/);
    if (match) properties[match[1]] = value;
  }
  if (Object.keys(properties).length) line.properties = properties;

  const body = { items: [line] };

  if (button) button.disabled = true;
  feedback(button, BUSY);

  serial(async () => {
    try {
      await post(ADD_URL, body);
      await settle(await readCart());
      feedback(button, DONE, 1800);

      /* The confirmation. Skipped on /cart, where the shopper is already
         looking at the whole cart and a panel over it would only cover the
         line that just changed. settle() has already re-rendered the
         drawer, so what slides in is current. */
      if (!document.querySelector('[data-cart-page]')) openDrawer(button);

      /* An add from inside the drawer (You may also like) re-renders the
         button away. Focus goes to the panel rather than being dropped to
         <body>, behind the dialog. */
      if (button && !button.isConnected && drawerIsOpen()) {
        const panel = drawer().querySelector('[data-cart-drawer-panel]');
        if (panel) panel.focus();
      }
    } catch (error) {
      feedback(button, null);
      showError(form, error.message);
    } finally {
      if (button) button.disabled = false;
    }
  });
});

/* ---------- the /cart page: quantity, remove, clear ---------- */

function lineOf(el) {
  const row = el.closest('[data-cart-line]');
  return row ? Number(row.dataset.cartLine) : null;
}

/** /cart/change.js addresses lines by 1-based index, not by key. */
function changeLine(line, quantity) {
  return serial(async () => {
    try {
      const body = { line, quantity };
      const ids = cartSectionIds();
      // Asks Shopify to render the cart surfaces as PART of this same
      // response, so the totals they produce can never be a request behind
      // the change that's driving them — see renderSections() above.
      if (ids) body.sections = ids;
      await settle(await post(CHANGE_URL, body));
    } catch (error) {
      console.error('[cart]', error);
      // the row still shows the old number, which is the truth until the
      // shopper tries again — better than a silent wrong quantity
      await renderSections();
    }
  });
}

document.addEventListener('click', event => {
  const step = event.target.closest('[data-qty-decrease], [data-qty-increase]');
  if (step) {
    const stepper = step.closest('[data-qty-stepper]');
    const input = stepper && stepper.querySelector('[data-qty-input]');
    const line = lineOf(step);
    if (!input || !line) return;

    const by = step.hasAttribute('data-qty-increase') ? 1 : -1;
    const next = Math.max(0, (parseInt(input.value, 10) || 0) + by);
    input.value = next;   // optimistic, so the stepper feels immediate
    changeLine(line, next);
    return;
  }

  const remove = event.target.closest('[data-cart-remove]');
  if (remove) {
    event.preventDefault();
    const line = lineOf(remove);
    if (line) changeLine(line, 0);
    return;
  }

  const clear = event.target.closest('[data-cart-clear]');
  if (clear) {
    event.preventDefault();
    serial(async () => {
      try {
        const ids = cartSectionIds();
        await settle(await post(CLEAR_URL, ids ? { sections: ids } : {}));
      } catch (error) {
        console.error('[cart]', error);
      }
    });
  }
});

/* ---------- purchase option: one-time or subscribe, per line ----------
   The drawer's toggle and frequency picker (sections/cart-drawer.liquid).
   /cart/change.js takes `selling_plan` alongside the line: a plan id
   subscribes the line, null makes it a one-time purchase again. Quantity is
   sent too, read off the line's own stepper, so the change never resets it.

   The drawer re-renders wholesale, which drops focus to <body>. The control
   that was used is found again in the new markup by its line and role and
   focused, so a keyboard or screen reader user stays where they were. */

function changePlan(control, plan) {
  const row = control.closest('[data-cart-line]');
  const line = lineOf(control);
  if (!row || !line) return;

  const qtyInput = row.querySelector('[data-qty-input]');
  const quantity = Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);
  /* Optimistic: the tab, the content under it and the price all switch now,
     from markup already on the page, and the cart saves behind them. The
     re-render that follows arrives in this same state, so nothing moves
     twice. If the save fails, the re-render puts the truth back. */
  if (control.matches('[data-cart-plan]')) {
    row.dataset.planState = plan ? 'sub' : 'once';
    control.parentElement.querySelectorAll('[data-cart-plan]').forEach(button => {
      button.setAttribute('aria-pressed', button === control ? 'true' : 'false');
    });
  }

  const refocus = control.matches('[data-cart-plan-select]')
    ? '[data-cart-plan-select]'
    : `[data-cart-plan="${control.dataset.cartPlan}"]`;

  serial(async () => {
    try {
      const body = { line, quantity, selling_plan: plan || null };
      const ids = cartSectionIds();
      if (ids) body.sections = ids;
      await settle(await post(CHANGE_URL, body));
    } catch (error) {
      console.error('[cart]', error);
      await renderSections();
    }
    const again = document.querySelector(`[data-cart-drawer] [data-cart-line="${line}"] ${refocus}`);
    if (again) again.focus();
  });
}

document.addEventListener('click', event => {
  const button = event.target.closest('[data-cart-plan]');
  if (!button || button.getAttribute('aria-pressed') === 'true') return;
  changePlan(button, button.dataset.cartPlan === 'sub' ? button.dataset.planId : null);
});

document.addEventListener('change', event => {
  const select = event.target.closest('[data-cart-plan-select]');
  if (select) changePlan(select, select.value);
});

/* ---------- pack size, per line ----------
   The drawer's size picker, shown only while its A/B-test setting is off.
   A cart line cannot change variant, so the new size is ADDED first -- same
   quantity, same subscription plan -- and the old line is removed only once
   that succeeded, by its key (keys, unlike indexes, survive the add). A size
   that fails to add leaves the cart untouched and says why.

   The plan is sent as the plan id the line already has. Every size of a
   drink carries the same plans, which is what lets the id travel. */

document.addEventListener('change', event => {
  const select = event.target.closest('[data-cart-size-select]');
  if (!select) return;

  const row = select.closest('[data-cart-line]');
  if (!row || select.value === row.dataset.variantId) return;

  const variantId = select.value;
  const qtyInput = row.querySelector('[data-qty-input]');
  const quantity = Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);
  const planSelect = row.querySelector('[data-cart-plan-select]');
  const plan = row.dataset.planState === 'sub' && planSelect ? planSelect.value : null;
  const key = row.dataset.lineKey;

  select.disabled = true;

  serial(async () => {
    try {
      const line = { id: variantId, quantity };
      if (plan) line.selling_plan = plan;
      await post(ADD_URL, { items: [line] });

      const body = { id: key, quantity: 0 };
      const ids = cartSectionIds();
      if (ids) body.sections = ids;
      await settle(await post(CHANGE_URL, body));
    } catch (error) {
      console.error('[cart]', error);
      await renderSections();
      const row = document.querySelector(`[data-cart-drawer] [data-line-key="${key}"] .cart-drawer__body`);
      if (row) {
        const note = document.createElement('p');
        note.className = 'cart-error';
        note.setAttribute('role', 'alert');
        note.textContent = error.message;
        row.append(note);
      }
      return;
    }
    const again = document.querySelector(`[data-cart-drawer] [data-variant-id="${variantId}"] [data-cart-size-select]`);
    if (again) again.focus();
  });
});

document.addEventListener('change', event => {
  const input = event.target.closest('[data-qty-input]');
  if (!input) return;
  const line = lineOf(input);
  if (!line) return;
  changeLine(line, Math.max(0, parseInt(input.value, 10) || 0));
});

/* The cart form must never submit: its native path is a full page load,
   which is the thing this file exists to avoid. Checkout is the one
   button allowed through, because checkout IS a navigation. */
document.addEventListener('submit', event => {
  const form = event.target.closest('[data-cart-form]');
  if (!form) return;

  /* activeElement is the fallback for browsers without event.submitter
     (Safari below 15.4). Getting this wrong swallows the checkout click,
     so it is worth the second check rather than the shorter line. */
  const submitter = event.submitter || document.activeElement;
  if (submitter && submitter.name === 'checkout') return;

  event.preventDefault();
});

/* ---------- coupons ----------
   Still a navigation, and not for want of trying: Shopify has no
   storefront endpoint that applies a discount code to the cart. The
   /discount/<code> route is the only way, and it redirects. Delegated
   anyway so it survives the section swaps above. */

function applyCoupon(root) {
  const input = root.querySelector('[data-coupon-input]');
  const code = input && input.value.trim();
  if (!code) return;
  const back = root.dataset.cartUrl || CART_URL;
  window.location.href = `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(back)}`;
}

document.addEventListener('click', event => {
  const submit = event.target.closest('[data-coupon-submit]');
  if (submit) applyCoupon(submit.closest('[data-coupon-form]'));
});

document.addEventListener('keydown', event => {
  if (event.key !== 'Enter') return;
  const input = event.target.closest('[data-coupon-input]');
  if (!input) return;
  event.preventDefault();
  applyCoupon(input.closest('[data-coupon-form]'));
});

/* ---------- first paint ----------
   Liquid already rendered the right number, but a cart can change in
   another tab, and back/forward can serve this page from the bfcache
   with a stale badge. */

readCart().then(cart => paintCount(cart.item_count)).catch(() => {});
window.addEventListener('pageshow', event => {
  if (event.persisted) readCart().then(cart => paintCount(cart.item_count)).catch(() => {});
});
